import { z } from "zod"

/**
 * Planned markdown per channel type and the margin it implies. Bottom-up: absent means full price,
 * never inherited. Reporting only, never changes the budget amount. Client-side so the margin
 * column follows each keystroke; the server supplies only the per-type basis.
 */

/** A `Channel.type` value, or the channel id where the channel declares none. */
export type ChannelTypeKey = string

export type ChannelTypeBasis = {
  key: ChannelTypeKey
  label: string
  /** Target channels a channel type groups; empty for an untyped channel, which is its own. */
  channels: string[]
  /** Share of the budget's allocation, 0..1. */
  weight: number
  /** Margin before any markdown, 0..1. `null` without both a cost and a list price. */
  fullPriceMargin: number | null
}

/** Which target channels a channel type covers; `undefined` for an untyped channel. */
export const channelCoverage = (type: ChannelTypeBasis): string | undefined =>
  type.channels.length > 0 ? `Covers ${type.channels.join(", ")}` : undefined

/** Channel types in display order. */
export type PlanningBasis = {
  channelTypes: ChannelTypeBasis[]
}

export type PeriodMarkdowns = Record<ChannelTypeKey, number | null>

/** `BudgetDistribution.markdowns` is stored JSON: anything but a numeric fraction is dropped. */
export const periodMarkdownsSchema = z
  .record(z.string(), z.unknown())
  .transform(
    (raw): PeriodMarkdowns =>
      Object.fromEntries(
        Object.entries(raw).filter(
          (entry): entry is [string, number] => typeof entry[1] === "number",
        ),
      ),
  )
  .nullable()
  .catch(null)
  .catch(null)

const effectiveMarkdown = (entered: number | null | undefined): number => entered ?? 0

/** `NumericInput` emits 0 on clear, so 0% means unset (full price). */
export const isPlannedMarkdown = (v: number | null | undefined): v is number => v != null && v > 0

/** Unset, null and 0 all mean full price, so they compare equal. */
export const isSameMarkdowns = (a: PeriodMarkdowns, b: PeriodMarkdowns): boolean => {
  const planned = (m: PeriodMarkdowns, key: ChannelTypeKey) =>
    isPlannedMarkdown(m[key]) ? m[key] : null
  return [...new Set([...Object.keys(a), ...Object.keys(b)])].every(
    (key) => planned(a, key) === planned(b, key),
  )
}

/** Cost is unchanged, so the whole discount comes out of the margin. `null` at markdown >= 100%. */
export const marginAtMarkdown = (
  fullPriceMargin: number | null,
  markdown: number,
): number | null => {
  if (fullPriceMargin == null || markdown >= 1) return null
  return 1 - (1 - fullPriceMargin) / (1 - markdown)
}

/** Margin of one channel type in one period at its planned markdown. */
export const channelPeriodMargin = (
  type: ChannelTypeBasis,
  markdowns: PeriodMarkdowns,
): number | null => marginAtMarkdown(type.fullPriceMargin, effectiveMarkdown(markdowns[type.key]))

/**
 * Allocation-weighted margin across channel types, renormalised over types that can answer (have
 * cost + list price) so a missing one doesn't drag the blend toward zero. `null` when none can.
 */
export const blendedPeriodMargin = (
  basis: PlanningBasis | undefined,
  markdowns: PeriodMarkdowns,
): number | null => {
  if (!basis) return null
  let weighted = 0
  let covered = 0
  for (const type of basis.channelTypes) {
    const margin = channelPeriodMargin(type, markdowns)
    if (margin == null) continue
    weighted += margin * type.weight
    covered += type.weight
  }
  return covered > 0 ? weighted / covered : null
}

/** One budget period: its share of the allocation and its planned markdown. */
export type WeightedPeriod = { weight: number; markdowns: PeriodMarkdowns }

/** Weighted by allocation over periods that have allocation and an answer. `null` when none do. */
const allocationWeighted = (
  periods: WeightedPeriod[],
  valueOf: (markdowns: PeriodMarkdowns) => number | null,
): number | null => {
  let weighted = 0
  let covered = 0
  for (const p of periods) {
    const value = valueOf(p.markdowns)
    if (value == null || p.weight <= 0) continue
    weighted += value * p.weight
    covered += p.weight
  }
  return covered > 0 ? weighted / covered : null
}

const periodMarkdown = (basis: PlanningBasis, markdowns: PeriodMarkdowns): number =>
  basis.channelTypes.reduce((sum, t) => sum + effectiveMarkdown(markdowns[t.key]) * t.weight, 0)

/** Allocation-weighted across periods and channel types. 0 when nothing is planned. */
export const overallPlannedMarkdown = (
  basis: PlanningBasis | undefined,
  periods: WeightedPeriod[],
): number => {
  if (!basis || basis.channelTypes.length === 0 || periods.length === 0) return 0
  const totalWeight = periods.reduce((sum, p) => sum + p.weight, 0)
  if (totalWeight <= 0) return 0
  return periods.reduce(
    (sum, p) => sum + periodMarkdown(basis, p.markdowns) * (p.weight / totalWeight),
    0,
  )
}

/**
 * Allocation-weighted {@link blendedPeriodMargin} across periods, renormalised over periods that can
 * answer. No stored periods = blended full-price margin.
 */
export const overallPlannedMargin = (
  basis: PlanningBasis | undefined,
  periods: WeightedPeriod[],
): number | null =>
  periods.length === 0
    ? blendedPeriodMargin(basis, {})
    : allocationWeighted(periods, (markdowns) => blendedPeriodMargin(basis, markdowns))

export const hasPlannedMarkdown = (
  markdowns: PeriodMarkdowns,
  keys: readonly ChannelTypeKey[],
): boolean => keys.some((key) => isPlannedMarkdown(markdowns[key]))

export const hasAnyPlannedMarkdown = (periods: { markdowns: PeriodMarkdowns | null }[]): boolean =>
  periods.some((p) => Object.values(p.markdowns ?? {}).some(isPlannedMarkdown))

/** A type's share of each period is fixed, so allocation weighting is its sales weighting too. */
export const seasonChannelMarkdown = (key: ChannelTypeKey, periods: WeightedPeriod[]): number =>
  allocationWeighted(periods, (markdowns) => effectiveMarkdown(markdowns[key])) ?? 0

/** `null` without a full-price margin. No stored periods = its full-price margin. */
export const seasonChannelMargin = (
  type: ChannelTypeBasis,
  periods: WeightedPeriod[],
): number | null =>
  periods.length === 0
    ? type.fullPriceMargin
    : allocationWeighted(periods, (markdowns) => channelPeriodMargin(type, markdowns))

/**
 * The budget's season margin for one channel type (`null` = all), as its Total margin and Target
 * profit margin read it: weighted by its channel split and period allocation.
 */
export const budgetSeasonMargin = (
  channelTypes: ChannelTypeBasis[],
  periods: WeightedPeriod[],
  channel: ChannelTypeKey | null,
): number | null => {
  if (channel == null) return overallPlannedMargin({ channelTypes }, periods)
  const type = channelTypes.find((t) => t.key === channel)
  return type ? seasonChannelMargin(type, periods) : null
}

/** Last year's sales of costed SKUs for one channel type in one period. */
export type LastYearCost = { costValue: number; costedRevenue: number }

export type PeriodLastYear = Record<ChannelTypeKey, LastYearCost>

/** No cost or no revenue recorded means no answer, not 0% or 100%. */
export const realizedMargin = (cost: LastYearCost | undefined): number | null =>
  cost == null || cost.costValue === 0 || cost.costedRevenue === 0
    ? null
    : 1 - cost.costValue / cost.costedRevenue

/** Positive revenue and a margin within 0..100%. */
const isUsable = (cost: LastYearCost | undefined): cost is LastYearCost =>
  cost != null && cost.costValue > 0 && cost.costValue <= cost.costedRevenue

/**
 * Net returns or a margin outside 0..100%: too little volume to compare against. Shown as a flag and
 * left out of every average.
 */
export const isLowLastYearVolume = (cost: LastYearCost | undefined): boolean =>
  realizedMargin(cost) != null && !isUsable(cost)

/** Margin of the summed pairs, which weights each pair's margin by its LY revenue. Skips low volume. */
const pooledMargin = (costs: (LastYearCost | undefined)[]): number | null => {
  let costValue = 0
  let costedRevenue = 0
  for (const cost of costs) {
    if (!isUsable(cost)) continue
    costValue += cost.costValue
    costedRevenue += cost.costedRevenue
  }
  return realizedMargin({ costValue, costedRevenue })
}

/** Across every channel type, regardless of the channel filter. */
export const periodLastYearMargin = (lastYear: PeriodLastYear): number | null =>
  pooledMargin(Object.values(lastYear))

export const seasonChannelLastYearMargin = (
  key: ChannelTypeKey,
  periods: PeriodLastYear[],
): number | null => pooledMargin(periods.map((p) => p[key]))

export const seasonLastYearMargin = (periods: PeriodLastYear[]): number | null =>
  pooledMargin(periods.flatMap((p) => Object.values(p)))

/** Hash of the Markdown impact section on a collection's Financial reconciliation tab. */
export const MARKDOWN_IMPACT_ANCHOR = "markdown-impact"
